import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { HandlingWarningFields } from "../components/HandlingWarningFields";
import {
  CustomItemDetailsFields,
  customItemPayload,
  newCustomItemDetails
} from "../components/CustomItemDetailsFields";
import {
  newToteDetails,
  ToteDetailsFields,
  toteDetailsPayload
} from "../components/ToteDetailsFields";
import type { Location } from "../types";

const types = [
  ["TOTE", "Tote"],
  ["BOX", "Box"],
  ["CRATE", "Crate"],
  ["TOOL", "Tool case"],
  ["EQUIP", "Equipment case"],
  ["FURN", "Furniture"],
  ["ELEC", "Electronics"],
  ["MISC", "Other"]
];

export function NewInventoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [locations, setLocations] = useState<Location[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toteDetails, setToteDetails] = useState(newToteDetails);
  const [customDetails, setCustomDetails] = useState(() => newCustomItemDetails("MISC"));
  const [form, setForm] = useState({
    inventory_type: "TOTE",
    name: "",
    description: "",
    current_location_id: "",
    destination_room_id: "",
    fragile: false,
    heavy: false,
    keep_upright: false,
    do_not_stack: false,
    temperature_sensitive: false,
    lithium_batteries: false,
    generate_nfc_link: true
  });

  useEffect(() => {
    api.locations().then(setLocations);
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const unit = await api.createInventory({
        ...form,
        ...(form.inventory_type === "TOTE"
          ? toteDetailsPayload(toteDetails)
          : customItemPayload(customDetails)),
        current_location_id: form.current_location_id || null,
        destination_room_id: form.destination_room_id || null,
        tag_token: search.get("tag") || null,
        generate_nfc_link:
          user?.role === "administrator" && !search.get("tag") && form.generate_nfc_link
      });
      navigate(`/inventory/${unit.id}`, { replace: true });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not create inventory");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page narrow-page">
      <div className="page-heading">
        <div>
          <h1>{search.get("tag") ? "Register this tag" : "New inventory unit"}</h1>
          <p>A stock number is generated automatically and will never be silently reused.</p>
        </div>
      </div>
      <form className="editor-form" onSubmit={submit}>
        <div className="form-grid two">
          <label>
            Inventory type
            <select
              value={form.inventory_type}
              onChange={(event) => {
                const inventory_type = event.target.value;
                setForm({ ...form, inventory_type });
                if (inventory_type !== "TOTE") setCustomDetails(newCustomItemDetails(inventory_type));
              }}
            >
              {types.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Short inventory name
            <input
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              placeholder="Garage electronics"
              required
            />
          </label>
        </div>
        <label>
          Description
          <textarea
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
            placeholder="A quick description of the container and its purpose"
          />
        </label>
        {form.inventory_type === "TOTE" && (
          <ToteDetailsFields value={toteDetails} onChange={setToteDetails} />
        )}
        {form.inventory_type !== "TOTE" && (
          <CustomItemDetailsFields value={customDetails} onChange={setCustomDetails} />
        )}
        <div className="form-grid two">
          <label>
            Current location
            <select
              value={form.current_location_id}
              onChange={(event) => setForm({ ...form, current_location_id: event.target.value })}
            >
              <option value="">Not set</option>
              {locations
                .filter((location) => location.group !== "destination")
                .map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name} · {location.group}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Destination room
            <select
              value={form.destination_room_id}
              onChange={(event) => setForm({ ...form, destination_room_id: event.target.value })}
            >
              <option value="">Not set</option>
              {locations
                .filter((location) => location.group === "destination")
                .map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
            </select>
          </label>
        </div>
        <HandlingWarningFields
          value={form}
          onChange={(field, checked) => setForm({ ...form, [field]: checked })}
        />
        {user?.role === "administrator" && !search.get("tag") && (
          <fieldset className="nfc-create-choice">
            <legend>NFC tag</legend>
            <label className="large-check">
              <input
                type="checkbox"
                checked={form.generate_nfc_link}
                onChange={(event) =>
                  setForm({ ...form, generate_nfc_link: event.target.checked })
                }
              />
              <span>
                Generate a unique NFC scan link
                <small>
                  The link will be assigned to this inventory unit and ready to copy after saving.
                </small>
              </span>
            </label>
          </fieldset>
        )}
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="button" className="button secondary" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button className="button primary" disabled={saving}>
            {saving ? "Creating…" : "Create inventory"}
          </button>
        </div>
      </form>
    </div>
  );
}
